import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, createApiClient, newIdempotencyKey } from "./api.js";
import { SessionContext } from "./session.js";
const MAX_TIMER = 2 ** 31 - 1;
const SAY_PASSWORD = "This app signs in with an email and a password.";
/**
 * Session from the server's sign-in. The browser holds no token: the session is an HttpOnly cookie that scripts cannot
 * read, so there is nothing here for a script (or a browser extension, or an injected one) to steal. What the app knows
 * about the person comes from `GET /v1/auth/me`, for display and for hiding what they cannot use; the server still decides
 * every request. A password is held only while the form is being sent and is never put in storage.
 *
 * It fills the same context as the development-token provider, so the rest of an app does not change.
 */
export function PasswordSessionProvider({ children, apiBase = "", fetchImpl, now = Date.now }) {
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState(null);
    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const api = useMemo(() => createApiClient({ baseUrl: apiBase, getToken: () => null, ...(fetchImpl && { fetchImpl }) }), [apiBase, fetchImpl]);
    useEffect(() => {
        let cancelled = false;
        api.get("/v1/auth/me").then((m) => { if (!cancelled) {
            setMe(m);
            setLoading(false);
        } }, () => { if (!cancelled) {
            setMe(null);
            setLoading(false);
        } });
        return () => { cancelled = true; };
    }, [api]);
    const clear = useCallback(() => { setMe(null); }, []);
    const signOut = useCallback(() => {
        clear();
        api.post("/v1/auth/logout", undefined, { idempotencyKey: newIdempotencyKey() }).catch(() => undefined); // the cookie is cleared by the answer; failing to reach the server must not keep the page signed in
    }, [api, clear]);
    const login = useCallback(async (email, password) => {
        const r = await api.post("/v1/auth/login", { email, password }, { idempotencyKey: newIdempotencyKey() });
        if (!alive.current)
            return;
        setNotice(null);
        setMe({ ...r.user, expires_at: r.expires_at });
    }, [api]);
    const changePassword = useCallback(async (current, next) => {
        await api.post("/v1/auth/password", { current_password: current, new_password: next }, { idempotencyKey: newIdempotencyKey() });
        if (!alive.current)
            return;
        setNotice("Your password was changed. Sign in again with the new password.");
        clear(); // the server ended every session, this one too
    }, [api, clear]);
    const claims = useMemo(() => {
        if (!me)
            return null;
        const exp = me.expires_at ? Math.floor(Date.parse(me.expires_at) / 1000) : Math.floor(now() / 1000) + 3600;
        // Until the one-time password is changed the server gives the session no roles, so the page shows none either.
        return { sub: me.user_id, kind: me.kind, tenant_id: me.tenant_id, roles: me.must_change_password ? [] : me.roles, exp };
    }, [me, now]);
    useEffect(() => {
        if (!claims)
            return;
        const timer = setTimeout(signOut, Math.min(Math.max(claims.exp * 1000 - now(), 0), MAX_TIMER));
        return () => clearTimeout(timer);
    }, [claims, signOut, now]);
    const password = useMemo(() => ({
        loading, mustChangePassword: !!me?.must_change_password, notice, email: me?.email ?? null, login, changePassword,
    }), [loading, me, notice, login, changePassword]);
    const value = useMemo(() => ({
        token: null, claims, signOut, password,
        signIn: () => { throw new Error(SAY_PASSWORD); },
    }), [claims, signOut, password]);
    return _jsx(SessionContext.Provider, { value: value, children: children });
}
/** Turns a failed sign-in into a sentence that says what to do and nothing the server should not reveal. */
export function describeSignInError(e) {
    if (e instanceof ApiError) {
        if (e.code === "invalid_credentials")
            return "The email or password is not right.";
        if (e.category === "RateLimited")
            return "Too many attempts. Wait a few minutes and try again.";
        if (e.category === "DependencyUnavailable" || e.category === "Timeout")
            return e.message;
    }
    return "Could not sign in. Try again.";
}
